import React, { useCallback, useEffect, useMemo, useState } from "react";
import NavV2 from "@ui/NavV2";
import t from "../../../../i18n";
import { css, cx } from "@linaria/core";

import { useDashboards, type Dashboard } from "../../Dashboards/hook/api/dashboard";
import type { Project } from "../../../../api/useProjects";
import { usePut } from "../../../../api/useCall";

import DashboardTabs from "./DashboardTabs";
import DashboardPopup from "./DashboardTabs/DashboardPopup";
import {
  AddCircleLine,
  ArrowLeftLine,
  ArrowRightLine,
  ArrowRightUpLine,
  CloseLine,
  DownloadLine,
  More2Fill,
} from "@ui/IconV2/react";
import Menu from "@ui/Menu";
import Option, { OptionGroup } from "@ui/Option";
import { colors } from "@ui/Theme";
import ButtonIconV2 from "@ui/ButtonIconV2";
import Modal from "@ui/Modal";
import { useModals } from "@ui/lib/hooks/useModal";
import ModalFormRemoveDashboard from "./ModalFormRemoveDashboard";
import { useUserCanEdit } from "../../Dashboards/hook/useUserCanEdit";
import { SUMMARY_ID, type SummaryDashboard as OverrideSummaryDashboard } from "./utils";
import TooltipV2 from "@ui/TooltipV2";
import ExportPDFOption from "../../ExportPDFOption";
import { usePositionSummaryDashbardTabs } from "../hooks/usePositionTab";
import { useSummaryDashboards } from "../../../../api/useSummaryDashboards";
import { useMutateBody } from "../../../../api/useQuery";
import { useLocation, useNavigate, useParams } from "react-router";
import { useTranslations } from "../../../../api/useTranslations";
import { matchRouterPath, toPath } from "../../../../router/helpers/path";
import { useSettings } from "../../../../api/useSettings";
import { toBasenamePath } from "../../../../router/helpers/navigation";

export default function Navigation({ project }: { project: Project }) {
  const location = useLocation();
  const settings = useSettings();
  const translations = useTranslations();

  const remoteSummaryDashboards = useSummaryDashboards(project.id);
  const { dashboards: remoteDashboards } = useDashboards(project.id);

  const navigate = useNavigate();
  const updateSummaryDashboards = usePut("/projects/:projectId(uuid)/summary/dashboards");
  const mutateSummaryDashboards = useMutateBody("/projects/:projectId(uuid)/summary/dashboards", {
    routeParams: { projectId: project.id },
  });

  const canEdit = useUserCanEdit(project.id);

  const [menuOpen, setMenuOpen] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [dashboardToDelete, setDashboardToDelete] = useState<Dashboard | null>(null);
  const { modal, openModal, closeModal } = useModals<"remove-dashboard">();

  const { summaryPos, positionSummaryDashboards } = usePositionSummaryDashbardTabs(
    project.id,
    remoteDashboards,
    remoteSummaryDashboards,
  );

  const overrideSummaryDashboards = React.useMemo(
    () => remoteSummaryDashboards.map(({ createdAt, updatedAt, ...rest }) => rest),
    [remoteSummaryDashboards],
  );

  const publishedDashboards = React.useMemo(
    () => remoteDashboards.filter(d => d.published),
    [remoteDashboards],
  );

  const [summaryDashboards, setSummaryDashboards] =
    useState<OverrideSummaryDashboard[]>(overrideSummaryDashboards);

  const allPositions = useMemo(
    () => [...summaryDashboards.map(o => o.position), summaryPos].sort((a, b) => a - b),
    [summaryDashboards, summaryPos],
  );

  const minPosition = useMemo(() => allPositions.filter(pos => pos !== -1)[0], [allPositions]);
  const maxPosition = allPositions[allPositions.length - 1];

  const isSummary = Boolean(
    matchRouterPath("/projects/:projectId/summary/summary", location.pathname),
  );
  const { dashboardId } = useParams();
  const currentDashboard = useMemo(
    () => summaryDashboards.find(o => o.dashboardId === dashboardId),
    [summaryDashboards, dashboardId],
  );
  const currentDashboardPos = currentDashboard?.position;

  const disableMoveLeft = isSummary
    ? summaryPos === minPosition
    : currentDashboardPos === minPosition;

  const disableMoveRight = isSummary
    ? summaryPos === maxPosition
    : currentDashboardPos === maxPosition;
  const disableRemove = isSummary || summaryDashboards.length === 0 || !dashboardId;

  const getRedirectPathAfterDashboardRemoval = useCallback(
    ({
      projectId,
      dashboardId,
      summaryDashboards,
      summaryPos,
    }: {
      projectId: string;
      dashboardId: string;
      summaryDashboards: OverrideSummaryDashboard[];
      summaryPos: number;
    }) => {
      const all = [
        ...summaryDashboards,
        ...(settings.HIDE_LEGACY_SUMMARY_PAGE
          ? []
          : [
              {
                id: `${projectId}-${SUMMARY_ID}`,
                projectId,
                dashboardId: SUMMARY_ID,
                position: summaryPos,
              },
            ]),
      ];

      const ordered = all.sort((a, b) => a.position - b.position);
      const index = ordered.findIndex(d => d.dashboardId === dashboardId);

      if (index === -1) {
        return toPath("/projects/:projectId/summary", { projectId });
      }

      const previous = index > 0 ? ordered[index - 1] : null;

      if (previous && previous.dashboardId !== SUMMARY_ID) {
        return toPath("/projects/:projectId/summary/dashboard/:dashboardId", {
          projectId,
          dashboardId: previous.dashboardId,
        });
      }

      const next = ordered[index + 1];
      if (index === 0 && next && next.dashboardId !== SUMMARY_ID) {
        return toPath("/projects/:projectId/summary/dashboard/:dashboardId", {
          projectId,
          dashboardId: next.dashboardId,
        });
      }

      return toPath("/projects/:projectId/summary", { projectId });
    },
    [settings.HIDE_LEGACY_SUMMARY_PAGE],
  );

  const commitBatch = useCallback(
    async (updates: { dashboardId: string; position: number }[]) => {
      if (updates.length === 0) return;

      const rollback = mutateSummaryDashboards(prevDashboards => {
        const updatesById = new Map(
          updates.map(({ dashboardId, position }) => [dashboardId, position]),
        );

        const nextDashboards = prevDashboards.map(dashboard => {
          const hasUpdate = updatesById.has(dashboard.dashboardId);
          if (!hasUpdate) return dashboard;
          return { ...dashboard, position: updatesById.get(dashboard.dashboardId)! };
        });

        return nextDashboards;
      });

      try {
        await updateSummaryDashboards({
          routeParams: { projectId: project.id },
          body: updates,
        });
      } catch (error) {
        rollback();
        throw error;
      }
    },
    [project.id, updateSummaryDashboards, mutateSummaryDashboards],
  );

  const move = useCallback(
    async (dir: "left" | "right", dashboardId?: string) => {
      const byPosition = new Map(summaryDashboards.map(o => [o.position, o]));
      const byId = new Map(summaryDashboards.map(o => [o.dashboardId, o]));

      const currentPos = isSummary
        ? summaryPos
        : dashboardId === undefined
          ? undefined
          : byId.get(dashboardId)?.position;

      if (currentPos === undefined) return;

      const allPositions = [...byPosition.keys(), summaryPos].sort((a, b) => a - b);
      const currentIndex = allPositions.indexOf(currentPos);
      const targetIndex = dir === "left" ? currentIndex - 1 : currentIndex + 1;
      const targetPos = allPositions[targetIndex];
      if (targetIndex < 0 || targetPos === -1 || targetPos === undefined) return;

      const dashboardToShift = byPosition.get(targetPos);
      const delta = dir === "left" ? -1 : 1;

      const updates: { dashboardId: string; position: number }[] = [];

      if (dashboardToShift) {
        const position = dashboardToShift.position - delta;
        updates.push({
          dashboardId: dashboardToShift.dashboardId,
          position: position < 0 ? 0 : position,
        });
      }

      if (!isSummary && dashboardId) updates.push({ dashboardId, position: targetPos });

      await commitBatch(updates);
    },
    [summaryDashboards, isSummary, summaryPos, commitBatch],
  );

  useEffect(() => {
    const isInvalidDashboard =
      dashboardId &&
      dashboardId !== SUMMARY_ID &&
      !publishedDashboards.some(d => d.id === dashboardId);
    if (isInvalidDashboard) {
      void navigate(toPath("/projects/:projectId/summary", { projectId: project.id }));
    }
  }, [publishedDashboards, project.id, navigate, dashboardId]);

  useEffect(() => setSummaryDashboards(overrideSummaryDashboards), [overrideSummaryDashboards]);

  return (
    <div className={cx(navigation, hideOnPrint)}>
      <NavV2>
        <DashboardTabs
          projectId={project.id}
          publishedDashboards={positionSummaryDashboards}
          canEdit={canEdit}
          dashboardPopup={
            <DashboardPopup
              projectId={project.id}
              open={popupOpen}
              setOpen={setPopupOpen}
              publishedDashboards={publishedDashboards}
              summaryDashboards={summaryDashboards}
              setSummaryDashboards={setSummaryDashboards}
              summaryPos={summaryPos}
            >
              <div>
                <TooltipV2
                  placement="bottom"
                  overlay={t("summary.dashboard.actions.add", translations)}
                >
                  <ButtonIconV2>
                    <AddCircleLine color={colors.carbon800} size="m" />
                  </ButtonIconV2>
                </TooltipV2>
              </div>
            </DashboardPopup>
          }
          contextualMenu={
            <Menu
              className={hideOnPrint}
              opened={menuOpen}
              setOpened={setMenuOpen}
              overlay={
                canEdit ? (
                  <OptionGroup>
                    <Option
                      disabled={isSummary}
                      onClick={() => {
                        if (!dashboardId) return;
                        setMenuOpen(false);
                        window.open(
                          toBasenamePath(
                            toPath("/projects/:projectId/dashboards/:dashboardId/edit", {
                              projectId: project.id,
                              dashboardId,
                            }),
                          ),
                        );
                      }}
                    >
                      <ArrowRightUpLine size="s" color={colors.carbon800} />
                      {t("summary.dashboard.actions.edit")}
                    </Option>
                    <Option
                      disabled={disableMoveLeft}
                      onClick={() => void move("left", dashboardId)}
                    >
                      <ArrowLeftLine size="s" color={colors.carbon800} />
                      {t("summary.dashboard.actions.move_left")}
                    </Option>

                    <Option
                      disabled={disableMoveRight}
                      onClick={() => void move("right", dashboardId)}
                    >
                      <ArrowRightLine size="s" color={colors.carbon800} />
                      {t("summary.dashboard.actions.move_right")}
                    </Option>

                    <ExportPDFOption>
                      <DownloadLine size="s" />
                      {t("summary.dashboard.actions.export_pdf")}
                    </ExportPDFOption>

                    <Option
                      disabled={disableRemove}
                      onClick={() => {
                        const d = publishedDashboards.find(x => x.id === dashboardId);
                        if (d) {
                          setDashboardToDelete(d);
                          setMenuOpen(false);
                          openModal("remove-dashboard");
                        }
                      }}
                      title={
                        isSummary
                          ? t("summary.dashboard.actions.remove.summary_disabled")
                          : undefined
                      }
                    >
                      <CloseLine size="s" color={colors.carbon800} />
                      {t("summary.dashboard.actions.remove")}
                    </Option>
                  </OptionGroup>
                ) : (
                  <OptionGroup>
                    <ExportPDFOption>
                      <DownloadLine size="s" />
                      {t("summary.dashboard.actions.export_pdf")}
                    </ExportPDFOption>
                  </OptionGroup>
                )
              }
            >
              <div
                onClickCapture={event => {
                  event.preventDefault();
                }}
              >
                <TooltipV2 placement="bottom" overlay={t("summary.dashboard.menu.tooltip.actions")}>
                  <ButtonIconV2>
                    <More2Fill size="m" />
                  </ButtonIconV2>
                </TooltipV2>
              </div>
            </Menu>
          }
        />
      </NavV2>

      {dashboardToDelete && (
        <Modal
          title={t("summary.dashboard.modal.remove.title", translations)}
          isOpen={modal === "remove-dashboard"}
          onRequestClose={() => {
            closeModal();
            setDashboardToDelete(null);
          }}
        >
          <ModalFormRemoveDashboard
            projectId={project.id}
            dashboardId={dashboardToDelete.id}
            dashboardName={dashboardToDelete.title}
            nextTab={getRedirectPathAfterDashboardRemoval({
              projectId: project.id,
              dashboardId: dashboardToDelete.id,
              summaryDashboards,
              summaryPos,
            })}
            onRequestClose={closeModal}
            summaryDashboards={summaryDashboards}
            setSummaryDashboards={setSummaryDashboards}
          />
        </Modal>
      )}
    </div>
  );
}

//#region CSS
const navigation = css`
  display: flex;
  flex-wrap: nowrap;
  width: 100%;

  & > :nth-child(1) {
    display: flex;
    width: 100%;
    & > :nth-child(5) {
      flex: 1;
    }
  }
`;
const hideOnPrint = css`
  @media print {
    display: none !important;
  }
`;
//#endregion CSS
